import * as THREE from 'three';

export const createSplitScreenRenderer = ({ renderer, scene, camera, width, height }) => {
    const leftRT  = new THREE.WebGLRenderTarget(width, height);
    const rightRT = new THREE.WebGLRenderTarget(width, height);

    const effectCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const effectScene  = new THREE.Scene();

    const effectMaterial = new THREE.ShaderMaterial({
        uniforms: {
            tLeft:       { value: leftRT.texture },
            tRight:      { value: rightRT.texture },
            uTime:       { value: 0 },
            uResolution: { value: new THREE.Vector2(width, height) },
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform sampler2D tLeft;
            uniform sampler2D tRight;
            uniform float uTime;
            uniform vec2 uResolution;
            varying vec2 vUv;

            void main() {
                float dynamicAmp  = 0.02 * sin(uTime * 0.5);
                float dynamicFreq = 10.0 + 2.0 * cos(uTime * 0.3);

                float wave1 = sin(vUv.y * dynamicFreq + uTime * 2.5);
                float wave2 = sin(vUv.y * 25.0 - uTime * 5.0) * 0.3;
                float wave3 = sin(vUv.y * 5.0 + uTime * 0.8) * 0.5;
                float wave  = (wave1 + wave2 + wave3) * dynamicAmp;

                float splitX   = 0.5 + wave;
                float dist      = abs(vUv.x - splitX);
                float thickness = 0.0005;
                float edge      = 0.001;
                float lineFactor = 1.0 - smoothstep(thickness, thickness + edge, dist);

                if (lineFactor > 0.0) {
                    vec4 lineColor = vec4(0, 0, 0, 0);
                    gl_FragColor = mix(
                        vUv.x < splitX ? texture2D(tLeft, vUv) : texture2D(tRight, vUv),
                        lineColor,
                        lineFactor
                    );
                } else if (vUv.x < splitX) {
                    gl_FragColor = texture2D(tLeft, vUv);
                } else {
                    gl_FragColor = texture2D(tRight, vUv);
                }
            }
        `,
    });

    const effectQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), effectMaterial);
    effectScene.add(effectQuad);

    const wireMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        wireframe: true,
        transparent: true,
        opacity: 0.6,
        depthWrite: true,
    });

    const renderFrame = ({
        sparkRenderer,
        characterInstances,
        floorDebugMesh,
        showDebugBodies,
        charMesh,
        elapsedTime,
        driveSparkUpdate,
        qualitySparkUpdateIntervalMs,
    }) => {
        // ── Left screen: realistic render ──
        showDebugBodies && (charMesh.visible = false);
        floorDebugMesh.visible = false;

        for (const inst of characterInstances) {
            inst.model.traverse(c => {
                if (c.isMesh && c.userData.origMat) c.material = c.userData.origMat;
            });
            inst.ragdollBodies?.forEach(rb => { if (rb.debugMesh) rb.debugMesh.visible = false; });
        }

        sparkRenderer.material.transparent = true;
        sparkRenderer.material.depthWrite  = false;
        sparkRenderer.falloff   = 1.0;
        sparkRenderer.minAlpha  = 0.5 / 255.0;

        driveSparkUpdate(qualitySparkUpdateIntervalMs);
        renderer.setRenderTarget(leftRT);
        renderer.clearColor();
        sparkRenderer.render(scene, camera);

        // ── Right screen: wireframe + hard-edge 3DGS ──
        showDebugBodies && (charMesh.visible = true);

        for (const inst of characterInstances) {
            inst.model.traverse(c => {
                if (c.isMesh && c.material) {
                    if (!c.userData.origMat) c.userData.origMat = c.material;
                    c.material = wireMat;
                }
            });
            inst.ragdollBodies?.forEach(rb => { if (rb.debugMesh) rb.debugMesh.visible = true; });
        }

        sparkRenderer.material.transparent = true;
        sparkRenderer.material.depthWrite  = true;
        sparkRenderer.material.depthTest   = true;
        sparkRenderer.sortRadial = false;
        sparkRenderer.falloff    = 0.0;
        sparkRenderer.minAlpha   = 0.65;

        renderer.setRenderTarget(rightRT);
        renderer.clearColor();
        sparkRenderer.render(scene, camera);

        // ── Composite to screen ──
        renderer.setRenderTarget(null);
        effectMaterial.uniforms.tLeft.value  = leftRT.texture;
        effectMaterial.uniforms.tRight.value = rightRT.texture;
        effectMaterial.uniforms.uTime.value  = elapsedTime;
        renderer.render(effectScene, effectCamera);
    };

    const onResize = (newW, newH) => {
        leftRT.setSize(newW, newH);
        rightRT.setSize(newW, newH);
        if (effectMaterial.uniforms.uResolution) {
            effectMaterial.uniforms.uResolution.value.set(newW, newH);
        }
    };

    return { leftRT, rightRT, effectMaterial, wireMat, renderFrame, onResize };
};
